// SigmaStudioProcessChart.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import "./SigmaStudio.css"; // Reuse same styles if possible

const SigmaStudioProcessChart = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // ─── Core Process Flow ───────────────────────────────────────
  const [processName, setProcessName] = useState("");
  const [steps, setSteps] = useState([]);

  // New step form fields
  const [newShortId, setNewShortId] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPredecessors, setNewPredecessors] = useState(""); // comma separated
  const [newSuccessors, setNewSuccessors] = useState("");   // comma separated
  const [newCondition, setNewCondition] = useState("");

  const [showChart, setShowChart] = useState(false);
  const chartRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addStep = () => {
    if (!newDescription.trim()) return;

    const newStep = {
      id: Date.now(),
      shortId: newShortId.trim() || `S${steps.length + 1}`,
      description: newDescription.trim(),
      predecessors: newPredecessors
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      successors: newSuccessors
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      condition: newCondition.trim(),
    };

    setSteps([...steps, newStep]);

    // Clear form
    setNewShortId("");
    setNewDescription("");
    setNewPredecessors("");
    setNewSuccessors("");
    setNewCondition("");
  };

  const updateStep = (id, field, value) => {
    setSteps((prev) =>
      prev.map((step) =>
        step.id === id ? { ...step, [field]: value } : step
      )
    );
  };

  const deleteStep = (id) => {
    setSteps((prev) => prev.filter((step) => step.id !== id));
  };

  const populateExampleData = () => {
    setProcessName("Self-Adhesive Label Manufacturing – End-to-End");

    const exampleSteps = [
      { shortId: "START", description: "Start – Order Receipt & Planning", predecessors: [], successors: ["RMRC"], condition: "" },
      { shortId: "RMRC", description: "Raw Material Receipt & Inspection", predecessors: ["START"], successors: ["UNWD"], condition: "Pass QC" },
      { shortId: "UNWD", description: "Unwind & Web Guiding / Tension Control", predecessors: ["RMRC"], successors: ["PRNT"], condition: "" },
      { shortId: "PRNT", description: "Multi-color Flexographic Printing", predecessors: ["UNWD"], successors: ["VARN","CFOIL"], condition: "Job spec" },
      { shortId: "VARN", description: "Varnish / Overprint Varnish Application", predecessors: ["PRNT"], successors: ["D CUT"], condition: "" },
      { shortId: "CFOIL", description: "Cold Foil Application (optional)", predecessors: ["PRNT"], successors: ["D CUT"], condition: "If foil required" },
      { shortId: "D CUT", description: "Rotary Die Cutting & Matrix Stripping", predecessors: ["VARN","CFOIL"], successors: ["SLIT"], condition: "" },
      { shortId: "SLIT", description: "Slitting to Final Width", predecessors: ["D CUT"], successors: ["INSPC"], condition: "" },
      { shortId: "INSPC", description: "100% Vision Inspection (AVT / Nikka)", predecessors: ["SLIT"], successors: ["REWND","REJCT"], condition: "" },
      { shortId: "REWND", description: "Rewinding & Finished Roll Preparation", predecessors: ["INSPC"], successors: ["PACK"], condition: "Pass" },
      { shortId: "REJCT", description: "Rejection / Rework Loop", predecessors: ["INSPC"], successors: ["PRNT","D CUT"], condition: "Fail → Rework" },
      { shortId: "PACK", description: "Packing, Labeling & Palletization", predecessors: ["REWND"], successors: ["DISP"], condition: "" },
      { shortId: "DISP", description: "Dispatch / Warehouse Transfer", predecessors: ["PACK"], successors: ["END"], condition: "" },
      { shortId: "END", description: "End – Delivery to Customer", predecessors: ["DISP"], successors: [], condition: "" },
    ];

    setSteps(exampleSteps.map(s => ({ ...s, id: Date.now() + Math.random() })));
  };

  // ─── Generate React Flow nodes & edges ────────────────────────────────
  const generateFlowElements = () => {
    const nodes = [];
    const edges = [];

    const xSpacing = 280;
    const ySpacing = 140;

    steps.forEach((step, index) => {
      const x = 100 + (index % 5) * xSpacing;
      const y = 80 + Math.floor(index / 5) * ySpacing;

      nodes.push({
        id: step.id.toString(),
        type: "default",
        position: { x, y },
        data: {
          label: (
            <div className="text-center p-2">
              <div className="font-bold text-teal-300">{step.shortId}</div>
              <div className="text-sm text-gray-200 mt-1">{step.description}</div>
              {step.condition && (
                <div className="text-xs text-amber-300 mt-1 italic">if: {step.condition}</div>
              )}
            </div>
          ),
        },
        style: {
          background: "#1e293b",
          border: "2px solid #64748b",
          borderRadius: "12px",
          width: 240,
          color: "white",
        },
      });

      // Predecessor → this (reverse of successors for easier rendering)
      step.predecessors.forEach((predShortId) => {
        const sourceStep = steps.find(s => s.shortId === predShortId);
        if (sourceStep) {
          edges.push({
            id: `e-${sourceStep.id}-${step.id}`,
            source: sourceStep.id.toString(),
            target: step.id.toString(),
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#94a3b8", strokeWidth: 2 },
            label: step.condition ? step.condition : undefined,
            labelStyle: { fill: "#fbbf24", fontSize: 11 },
          });
        }
      });
    });

    return { nodes, edges };
  };

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (showChart) {
      const { nodes: newNodes, edges: newEdges } = generateFlowElements();
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [showChart, steps]);

  const exportToPDF = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(`${processName || "Process-Flow"}.pdf`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-100 font-sans">
      {/* Header – same as your SIPOC */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500 px-4 py-3 flex justify-between items-center">
        <div>
          <div className="text-xl font-bold text-yellow-300">🗺️ Think5Whys Sigma Studio</div>
          <div className="text-xs text-gray-100">A Simple Process Flowchart Builder Playground</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded">
                Logout
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="px-3 py-1 border border-gray-600 rounded">
              Login
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      <main className="flex-1 p-4 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
          <button
            onClick={populateExampleData}
            className="px-6 py-3 bg-green-700 hover:bg-green-600 rounded-lg font-semibold"
          >
            Populate Adhesive Label Example
          </button>

          <button
            onClick={() => setShowChart(!showChart)}
            className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 rounded-lg font-semibold disabled:opacity-50"
            disabled={steps.length < 2}
          >
            {showChart ? "Hide Flowchart" : "View Process Flow Chart"}
          </button>

          {showChart && (
            <button
              onClick={exportToPDF}
              className="px-6 py-3 bg-teal-700 hover:bg-teal-600 rounded-lg font-semibold"
            >
              Export to PDF
            </button>
          )}
        </div>

        {/* Process Name */}
        <div className="mb-10 max-w-2xl mx-auto">
          <label className="block mb-2 text-cyan-200 font-medium">Process Name</label>
          <input
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            value={processName}
            onChange={(e) => setProcessName(e.target.value)}
            placeholder="e.g. Self-Adhesive Label Manufacturing – End-to-End"
          />
        </div>

        {/* Add Step Form */}
        <div className="bg-gray-900/70 p-6 rounded-xl border border-teal-900/60 mb-10">
          <h3 className="text-xl font-bold text-teal-300 mb-5">Add Process Step</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <input
              placeholder="Short ID (optional)"
              value={newShortId}
              onChange={(e) => setNewShortId(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded text-white"
            />
            <input
              placeholder="Step Description *"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded text-white"
            />
            <input
              placeholder="Predecessors (comma sep, short IDs)"
              value={newPredecessors}
              onChange={(e) => setNewPredecessors(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded text-white"
            />
            <input
              placeholder="Successors (comma sep, short IDs)"
              value={newSuccessors}
              onChange={(e) => setNewSuccessors(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded text-white"
            />
            <input
              placeholder="Condition / Decision"
              value={newCondition}
              onChange={(e) => setNewCondition(e.target.value)}
              className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded text-white"
            />
          </div>
          <button
            onClick={addStep}
            className="mt-5 px-6 py-2.5 bg-teal-700 hover:bg-teal-600 rounded-lg font-medium"
          >
            + Add Step
          </button>
        </div>

        {/* Steps List – Draggable */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {steps.length === 0 ? (
                <p className="text-center text-gray-500 py-10 italic">No steps added yet. Use example or add manually.</p>
              ) : (
                steps.map((step, index) => (
                  <SortableStep
                    key={step.id}
                    step={step}
                    index={index}
                    updateStep={updateStep}
                    deleteStep={deleteStep}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>

        {/* Flowchart */}
        {showChart && (
          <div ref={chartRef} className="mt-12 bg-gray-950 p-6 rounded-2xl border border-indigo-800/50 min-h-[700px]">
            <h2 className="text-2xl font-bold text-center text-indigo-300 mb-6">
              {processName || "Process Flow Diagram"}
            </h2>
            <div style={{ height: "650px" }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  fitView
                  minZoom={0.3}
                  maxZoom={2}
                  theme="dark"
                  style={{ background: "#0f172a" }}
                >
                  <Background variant="dots" gap={32} size={1.5} color="#475569" />
                  <Controls />
                  <MiniMap nodeColor="#64748b" />
                </ReactFlow>
              </ReactFlowProvider>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-black border-t border-blue-500 py-4 text-center text-sm text-gray-400">
        © 2026 Think5Whys Global DeepTech Solutions
      </footer>
    </div>
  );
};

// ─── Sortable Step Row ───────────────────────────────────────
const SortableStep = ({ step, index, updateStep, deleteStep }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex flex-col sm:flex-row gap-3 items-center p-4 bg-gray-800/90 rounded-lg border-l-4 border-teal-600 hover:bg-gray-700 transition cursor-grab active:cursor-grabbing"
    >
      <div className="w-10 text-center font-bold text-teal-300">{index + 1}</div>

      <input
        value={step.shortId}
        onChange={(e) => updateStep(step.id, "shortId", e.target.value)}
        placeholder="Short ID"
        className="w-24 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm"
      />

      <input
        value={step.description}
        onChange={(e) => updateStep(step.id, "description", e.target.value)}
        placeholder="Step description"
        className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-white"
      />

      <input
        value={step.predecessors.join(", ")}
        onChange={(e) => {
          const val = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
          updateStep(step.id, "predecessors", val);
        }}
        placeholder="Pred"
        className="w-28 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm"
      />

      <input
        value={step.successors.join(", ")}
        onChange={(e) => {
          const val = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
          updateStep(step.id, "successors", val);
        }}
        placeholder="Succ"
        className="w-28 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm"
      />

      <input
        value={step.condition}
        onChange={(e) => updateStep(step.id, "condition", e.target.value)}
        placeholder="Condition"
        className="w-40 px-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-amber-200 text-sm italic"
      />

      <button
        onClick={() => deleteStep(step.id)}
        className="text-red-400 hover:text-red-300 text-xl px-2"
      >
        🗑️
      </button>
    </div>
  );
};

export default SigmaStudioProcessChart;